import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Info } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface CreateAdminUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function CreateAdminUserModal({ open, onOpenChange, onSuccess }: CreateAdminUserModalProps) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: ''
  });
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    try {
      setCreating(true);

      // Validações
      if (!formData.email || !formData.password) {
        toast.error('Email e senha são obrigatórios');
        return;
      }

      if (formData.password.length < 8) {
        toast.error('A senha deve ter no mínimo 8 caracteres');
        return;
      }

      // Chamar edge function para criar usuário
      const { data, error } = await supabase.functions.invoke(
        'admin-create-user',
        {
          body: {
            email: formData.email,
            password: formData.password,
            full_name: formData.full_name,
            role: 'admin'
          }
        }
      );

      if (error) throw error;

      toast.success('Usuário admin criado com sucesso!');
      onSuccess();
      setFormData({ email: '', password: '', full_name: '' });

    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao criar usuário');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Criar Novo Usuário Admin</DialogTitle>
          <DialogDescription>
            Este usuário terá acesso completo ao painel administrativo
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="admin@exemplo.com"
              disabled={creating}
            />
          </div>

          <div>
            <Label htmlFor="password">Senha *</Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Mínimo 8 caracteres"
              disabled={creating}
            />
          </div>

          <div>
            <Label htmlFor="full_name">Nome Completo</Label>
            <Input
              id="full_name"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              placeholder="Nome do administrador"
              disabled={creating}
            />
          </div>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              O email será automaticamente confirmado e o usuário poderá fazer login imediatamente.
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={creating}
          >
            Cancelar
          </Button>
          <Button onClick={handleCreate} disabled={creating}>
            {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Criar Usuário
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
